import { z } from 'zod'

import { erpAccountSchema, erpClassAndLocationSchema } from './erpEntityTypes'

export enum MappingAccountType {
  Asset = 'Asset',
  Fee = 'Fee',
  GainLoss = 'Gain / Loss',
  InboundActivity = 'Inbound Activity',
  OutboundActivity = 'Outbound Activity',
  Token = 'Token',
  Oci = 'OCI',
  Impairment = 'Impairment',
  Class = 'Class',
  Location = 'Location',
}

export enum MappingSplitDistributionType {
  Full = 'full',
  Percent = 'percent',
  Fiat = 'fiat',
  Token = 'token',
}

export enum MappingType {
  Default = 'default',
  Category = 'category',
  Transaction = 'transaction',
  Wallet = 'wallet',
}

// Split mapping schemas
const fullSplitMappingSchema = z.object({
  type: z.literal(MappingSplitDistributionType.Full),
  account: erpAccountSchema,
})

export type FullSplitMapping = z.infer<typeof fullSplitMappingSchema>

const percentageSplitMappingSchema = z.object({
  type: z.literal(MappingSplitDistributionType.Percent),
  splits: z.array(
    z.object({
      percent: z.string(),
      account: erpAccountSchema,
    })
  ),
})

export type PercentageSplitMapping = z.infer<typeof percentageSplitMappingSchema>

const fiatSplitMappingSchema = z.object({
  type: z.literal(MappingSplitDistributionType.Fiat),
  splits: z.array(
    z.object({
      amount: z.string(),
      account: erpAccountSchema,
    })
  ),
  remainder: erpAccountSchema,
})

export type FiatSplitMapping = z.infer<typeof fiatSplitMappingSchema>

const tokenSplitMappingSchema = z.object({
  type: z.literal(MappingSplitDistributionType.Token),
  splits: z.array(
    z.object({
      asset: z.string(),
      amount: z.string(),
      account: erpAccountSchema,
    })
  ),
  remainder: erpAccountSchema,
})

export type TokenSplitMapping = z.infer<typeof tokenSplitMappingSchema>

export const splitMappingSchema = z.discriminatedUnion('type', [
  fullSplitMappingSchema,
  percentageSplitMappingSchema,
  fiatSplitMappingSchema,
  tokenSplitMappingSchema,
])

export type SplitMapping = z.infer<typeof splitMappingSchema>

export const erpInboundOutboundActivitySchema = z.union([erpAccountSchema, splitMappingSchema])

export type ErpInboundOutboundActivity = z.infer<typeof erpInboundOutboundActivitySchema>

// Mapping schema
export const accountingMappingSchema = z.object({
  [MappingAccountType.Asset]: erpAccountSchema.optional(),
  [MappingAccountType.Fee]: erpAccountSchema.optional(),
  [MappingAccountType.GainLoss]: erpAccountSchema.optional(),
  [MappingAccountType.InboundActivity]: erpInboundOutboundActivitySchema.optional(),
  [MappingAccountType.OutboundActivity]: erpInboundOutboundActivitySchema.optional(),
  [MappingAccountType.Token]: z.record(erpAccountSchema).optional(),
  [MappingAccountType.Oci]: erpAccountSchema.optional(),
  [MappingAccountType.Impairment]: erpAccountSchema.optional(),
  [MappingAccountType.Class]: erpClassAndLocationSchema.optional(),
  [MappingAccountType.Location]: erpClassAndLocationSchema.optional(),
})

export const defaultAccountingMappingSchema = accountingMappingSchema.extend({
  [MappingAccountType.InboundActivity]: erpAccountSchema.optional(),
  [MappingAccountType.OutboundActivity]: erpAccountSchema.optional(),
})

export type AccountingMapping = z.infer<typeof accountingMappingSchema>
export type DefaultAccountingMapping = z.infer<typeof defaultAccountingMappingSchema>

// Mappings schema
export const accountingMappingsSchema = z.object({
  [MappingType.Default]: defaultAccountingMappingSchema.optional(),
  [MappingType.Wallet]: z.record(accountingMappingSchema).optional(),
  [MappingType.Category]: z.record(accountingMappingSchema).optional(),
  [MappingType.Transaction]: z.record(accountingMappingSchema).optional(),
})

export type AccountingMappings = z.infer<typeof accountingMappingsSchema>
